import React, { useEffect, useRef, useState } from 'react'

import { BorderStyle, Box, Color, Space } from '@gousto-internal/citrus-react'
import { DeliveryInstructions } from 'DeliveryInstructions/DeliveryInstructions'
import { Recaptcha } from 'Recaptcha'
import { NullableReCAPTCHA } from 'Recaptcha/useChallengeClosingObserver'
import { useDispatch, useSelector } from 'react-redux'

import { actionTypes } from 'actions/actionTypes'
import statusActions from 'actions/status'
import { RibbonTriggerContainer } from 'components/RibbonTrigger'
import { DeliveryEducationBanner } from 'routes/Checkout/Components/Delivery/DeliveryEducationBanner'
import { ErrorMessage } from 'routes/Checkout/Components/ErrorMessage/ErrorMessage'
import { SectionHeader } from 'routes/Checkout/Components/SectionHeader'
import {
  initApplePaySession,
  loginAfterSignup,
  openLoginModal,
  trackClickPayWithApplePay,
} from 'routes/Checkout/checkoutActions'
import {
  getApplePayRecaptchaExecutionStarted,
  getDeliveryTimeAndDate,
} from 'routes/Checkout/checkoutSelectors'
import { usePricing } from 'routes/Menu/domains/pricing'
import {
  getDeliveryDetailsInstructions,
  getIsValidDeliveryDetailsInstructionsCustom,
} from 'selectors/deliveryDetails'

import css from './ApplePayButton.css'

const PayWithApplePay = () => {
  const dispatch = useDispatch()

  const { pricing } = usePricing()

  const { deliveryTime, deliveryDate } = useSelector(getDeliveryTimeAndDate)
  const deliveryInstructions = useSelector(getDeliveryDetailsInstructions)
  const isValidDeliveryDetailsInstructionsCustom = useSelector(
    getIsValidDeliveryDetailsInstructionsCustom,
  )

  const isDisabled = !deliveryInstructions || !isValidDeliveryDetailsInstructionsCustom

  // handler for Pay button
  const onPayClick = () => {
    dispatch({ type: actionTypes.APPLE_PAY_ERRORS_CLEAR })
    dispatch(statusActions.error(actionTypes.CHECKOUT_SIGNUP, null))
    dispatch(statusActions.error(actionTypes.USER_SUBSCRIBE, null))
    dispatch(trackClickPayWithApplePay())
    dispatch(initApplePaySession(pricing, deliveryDate, deliveryTime))
  }

  // ReCAPTCHA related fields
  const recaptchaRef = useRef<NullableReCAPTCHA>(null)
  const [hasGotRecaptchaToken, setHasGotRecaptchaToken] = useState(false)
  const recaptchaExecutionStarted = useSelector(getApplePayRecaptchaExecutionStarted)

  useEffect(() => {
    if (!recaptchaExecutionStarted) return

    if (recaptchaRef.current) recaptchaRef.current.execute()
    // in case feature isRecaptchaEnabled is switched off
    else dispatch(loginAfterSignup())
  }, [dispatch, recaptchaExecutionStarted])

  const onRecaptchaTokenChange = (token: string | null) => {
    // to react only on first reCAPTCHA token (when token is expired, this method is called with null)
    if (hasGotRecaptchaToken) return

    setHasGotRecaptchaToken(true)
    dispatch(loginAfterSignup(token))
  }

  const onRecaptchaChallengeClose = () => {
    if (!hasGotRecaptchaToken) {
      dispatch(statusActions.error(actionTypes.CHECKOUT_SIGNUP_LOGIN, true))
    }
  }

  return (
    <>
      <Box
        borderStyle={BorderStyle.Solid}
        borderWidth={0.5}
        borderColor={Color.ColdGrey_100}
        bg={Color.White}
        paddingH={[3, 6]}
        paddingV={[6, 8]}
        data-testing="checkoutApplePayDeliverySection"
      >
        <SectionHeader title="Delivery details" />
        <DeliveryInstructions />
        <DeliveryEducationBanner deliveryTime={deliveryTime} deliveryDate={deliveryDate} />
        <ErrorMessage onLoginClick={(e) => dispatch(openLoginModal(e))} />
        <button
          disabled={isDisabled}
          className={css.applePayButton}
          type="submit"
          onClick={onPayClick}
        >
          <span className={css.applePayButtonText}>Pay with</span>
          <span className={css.applePayButtonLogo} />
        </button>

        <Recaptcha
          ref={recaptchaRef}
          onChange={onRecaptchaTokenChange}
          onChallengeClose={onRecaptchaChallengeClose}
        />

        <RibbonTriggerContainer name="checkout-apple-pay" />
      </Box>
      <Space size={6} direction="vertical" />
    </>
  )
}

export { PayWithApplePay }
